import z from 'zod';

// ─── Option values for select fields ────────────────────────────────────────
// Each array defines the allowed values for a select field. The empty string
// represents the "not selected" default. When adding a new option, add it here
// and the validation + UI will both pick it up automatically.

export const testingTypeValues = ['', 'web', 'mobile', 'api', 'thick-client', 'iiot', 'other'] as const;
export const testingTypeOptions = [
  { label: '-- Select type --', value: '' },
  { label: 'Web application', value: 'web' },
  { label: 'Mobile application', value: 'mobile' },
  { label: 'API / Web service', value: 'api' },
  { label: 'Thick client', value: 'thick-client' },
  { label: '(I)IoT', value: 'iiot' },
  { label: 'Other', value: 'other' },
] as const satisfies readonly { label: string; value: (typeof testingTypeValues)[number] }[];

export const yesNoValues = ['', 'yes', 'no'] as const;
export const yesNoOptions = [
  { label: '-- Select --', value: '' },
  { label: 'Yes', value: 'yes' },
  { label: 'No', value: 'no' },
] as const satisfies readonly { label: string; value: (typeof yesNoValues)[number] }[];

export const environmentValues = ['', 'PRD', 'UAT', 'TST', 'GTU'] as const;
export const environmentOptions = [
  { label: '-- Select environment --', value: '' },
  { label: 'PRD (Production)', value: 'PRD' },
  { label: 'UAT (User Acceptance Testing)', value: 'UAT' },
  { label: 'TST (Testing)', value: 'TST' },
  { label: 'GTU', value: 'GTU' },
] as const satisfies readonly { label: string; value: (typeof environmentValues)[number] }[];

export const authUnauthValues = ['', 'unauthenticated', 'authenticated', 'both'] as const;
export const authUnauthOptions = [
  { label: '-- Select --', value: '' },
  { label: 'Unauthenticated', value: 'unauthenticated' },
  { label: 'Authenticated', value: 'authenticated' },
  { label: 'Both', value: 'both' },
] as const satisfies readonly { label: string; value: (typeof authUnauthValues)[number] }[];

export const frontendTypeValues = ['', 'web-application', 'sharepoint', 'web-service', 'fat-client', 'console', 'citrix', 'other'] as const;
export const frontendTypeOptions = [
  { label: '-- Select type --', value: '' },
  { label: 'Web application', value: 'web-application' },
  { label: 'SharePoint-based web application', value: 'sharepoint' },
  { label: 'Web service', value: 'web-service' },
  { label: 'FAT client (compiled workstation app)', value: 'fat-client' },
  { label: 'Console application (mainframe)', value: 'console' },
  { label: 'Citrix Application', value: 'citrix' },
  { label: 'Other', value: 'other' },
] as const satisfies readonly { label: string; value: (typeof frontendTypeValues)[number] }[];

export const cloudProviderValues = ['', 'none', 'aws', 'azure', 'gcp', 'digital-ocean', 'other'] as const;
export const cloudProviderOptions = [
  { label: '-- Select provider --', value: '' },
  { label: 'No cloud provider (internally hosted)', value: 'none' },
  { label: 'AWS', value: 'aws' },
  { label: 'Azure', value: 'azure' },
  { label: 'Google Cloud', value: 'gcp' },
  { label: 'Digital Ocean', value: 'digital-ocean' },
  { label: 'Other', value: 'other' },
] as const satisfies readonly { label: string; value: (typeof cloudProviderValues)[number] }[];

// ─── Shared form schema ─────────────────────────────────────────────────────
// This is the single source of truth for pentest intake form fields.
// Both the API route and the frontend form import this schema.

export const pentestIntakeSchema = z.object({
  // General Information (required fields)
  testingType: z.enum(testingTypeValues).refine((v) => v !== '', { message: 'Required' }),
  applicationName: z.string().min(1, 'Required'),
  applicationDescription: z.string().optional(),
  canProvideScreens: z.enum(yesNoValues).optional(),
  baseUrls: z.string().optional(),
  dynamicPagesCount: z.string().optional(),
  sensitiveUtilities: z.string().optional(),
  testEnvironment: z.enum(environmentValues).optional(),
  authenticationType: z.string().optional(),
  authOrUnauth: z.enum(authUnauthValues).optional(),
  timing: z.string().optional(),

  // Contact Info (required)
  contactName: z.string().min(1, 'Required'),
  contactEmail: z.string().email('Please enter a valid email address'),
  contactPhone: z.string().optional(),

  // Technical Information - Front-end
  frontendType: z.enum(frontendTypeValues).optional(),
  frontendLanguage: z.string().optional(),
  frontendFrameworks: z.string().optional(),

  // Technical Information - Middleware
  middlewareType: z.string().optional(),
  middlewareLanguage: z.string().optional(),

  // Technical Information - Backend
  backendType: z.string().optional(),
  backendLanguage: z.string().optional(),

  // Technical Information - External
  externalInterface: z.string().optional(),

  // Cloud Information
  cloudProvider: z.enum(cloudProviderValues).optional(),

  // Authentication Information
  requiresAuth: z.enum(yesNoValues).optional(),
  shouldTestAuth: z.enum(yesNoValues).optional(),
  authMechanism: z.string().optional(),
  canProvideCredentials: z.enum(yesNoValues).optional(),

  // Authorization Information
  multipleRoles: z.enum(yesNoValues).optional(),
  roles: z.string().optional(),
  rolesOverview: z.string().optional(),

  // General
  hasAiComponent: z.enum(yesNoValues).optional(),

  // Other
  specificFocus: z.string().optional(),
  hasUploadFeatures: z.enum(yesNoValues).optional(),
  approvalFlows: z.string().optional(),
  businessFlows: z.string().optional(),
  externalAccess: z.string().optional(),

  // Contact Information (detailed)
  appManagerContact: z.string().optional(),
  technicalContact: z.string().optional(),

  // Red Team
  redTeamNote: z.string().optional(),
}).strict();

export type PentestIntakePayload = z.infer<typeof pentestIntakeSchema>;
